import React, { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { Logo } from "./Logo";

const genId = () =>
  `sess-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const SUGGESTIONS = [
  "What services do you offer?",
  "What are your hours?",
  "How do I book a consultation?",
  "How can I apply for a job?",
];

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(genId);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hello! I'm Hylton, your virtual assistant at Hyltons Healthcare Solutions. How can I help you today? 💙",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading, open]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId, message: content });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "I'm sorry, I'm having trouble right now. Please call us at (617) 842-8426 or use the contact form.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 left-5 z-[60] w-[92vw] max-w-[400px] h-[560px] max-h-[75vh] bg-white rounded-3xl shadow-2xl border border-border flex flex-col overflow-hidden"
            data-testid="chat-panel"
          >
            {/* Header */}
            <div className="bg-brand-darkBlue px-5 py-4 flex items-center gap-3">
              <div className="bg-white rounded-full p-1.5">
                <Logo className="h-8 w-8" withText={false} />
              </div>
              <div className="flex-1">
                <p className="text-white font-heading font-semibold leading-tight">Hylton Assistant</p>
                <p className="text-white/70 text-xs flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-green-400 inline-block" /> Online now
                </p>
              </div>
              <button onClick={() => setOpen(false)} data-testid="chat-close" aria-label="Close chat" className="text-white/80 hover:text-white p-1">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-brand-lightBlue/40" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-3 text-[15px] leading-relaxed rounded-2xl ${
                      m.role === "user"
                        ? "bg-brand-red text-white rounded-br-md"
                        : "bg-white text-gray-800 border border-border rounded-bl-md"
                    }`}
                    data-testid={`chat-msg-${m.role}`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start" data-testid="chat-typing">
                  <div className="bg-white border border-border rounded-2xl rounded-bl-md px-4 py-3 flex gap-1.5">
                    {[0, 1, 2].map((d) => (
                      <motion.span
                        key={d}
                        className="w-2 h-2 rounded-full bg-brand-darkBlue/50"
                        animate={{ y: [0, -4, 0] }}
                        transition={{ duration: 0.7, repeat: Infinity, delay: d * 0.15 }}
                      />
                    ))}
                  </div>
                </div>
              )}
              {messages.length <= 1 && !loading && (
                <div className="pt-2 flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      data-testid="chat-suggestion"
                      className="text-sm text-brand-darkBlue bg-white border border-brand-darkBlue/30 rounded-full px-3 py-2 hover:bg-brand-darkBlue hover:text-white transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-border p-3 bg-white flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                rows={1}
                maxLength={2000}
                placeholder="Type your message..."
                data-testid="chat-input"
                className="flex-1 resize-none text-[15px] max-h-28 border-2 border-gray-300 focus:border-brand-darkBlue rounded-2xl px-4 py-3 outline-none"
              />
              <button
                onClick={() => send()}
                disabled={loading || !input.trim()}
                data-testid="chat-send"
                aria-label="Send message"
                className="shrink-0 w-12 h-12 rounded-full bg-brand-red text-white flex items-center justify-center hover:bg-brand-redDark transition-colors disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Launcher */}
      <motion.button
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-launcher"
        aria-label="Open chat assistant"
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-5 left-5 z-[60] w-16 h-16 rounded-full bg-brand-red text-white shadow-2xl flex items-center justify-center"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
              <X className="w-7 h-7" />
            </motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
              <MessageCircle className="w-7 h-7" />
            </motion.span>
          )}
        </AnimatePresence>
        {!open && (
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-green-400 border-2 border-white animate-pulse" />
        )}
      </motion.button>
    </>
  );
};
